import { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getTuboListadoClientes, updateTuboNegocio, getTuboGrafico, getTuboTabla, getTuboCincoDias, getTuboTablaDetalle, eliminarRegistroTubo } from '../api/apiConfig';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdReceipt } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import * as XLSX from 'xlsx';

const IngresarTuboNegocio = () => {
  const { user } = useUser();
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [monto, setMonto] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [tablaData, setTablaData] = useState([]);
  const [tablaLoading, setTablaLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 15;
  const [tuboCincoDiasData, setTuboCincoDiasData] = useState([]);
  const [tuboCincoDiasLoading, setTuboCincoDiasLoading] = useState(true);
  const [tuboDetalleData, setTuboDetalleData] = useState([]);
  const [tuboDetalleLoading, setTuboDetalleLoading] = useState(true);
  const [currentPageDetalle, setCurrentPageDetalle] = useState(1);
  const itemsPerPageDetalle = 15;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  /** Formatea el monto con separador de miles (.) mientras se escribe */
  const formatMontoDisplay = (value) => {
    if (value === '' || value == null) return '';
    const onlyDigitsAndComma = value.replace(/[^\d,]/g, '');
    const commaIndex = onlyDigitsAndComma.indexOf(',');
    let intPart = '';
    let decPart = '';
    if (commaIndex === -1) {
      intPart = onlyDigitsAndComma;
    } else {
      intPart = onlyDigitsAndComma.slice(0, commaIndex);
      decPart = onlyDigitsAndComma.slice(commaIndex + 1).replace(/\D/g, '').slice(0, 2);
    }
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decPart ? `${intFormatted},${decPart}` : intFormatted;
  };

  /** Convierte el monto mostrado (ej. "1.234.567") a número para enviar al API */
  const parseMontoValue = (str) => {
    if (!str || !str.trim()) return NaN;
    const normalized = str.replace(/\./g, '').replace(',', '.');
    return parseFloat(normalized);
  };

  const filterTablaData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.FEC_OPE, item.GRUPO, item.EJECUTIVO, item.RUT_CLI, item.CLIENTE, item.MON_ANT, item.MONTO_TUBO];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedTablaData = () => {
    let dataToSort = filterTablaData(tablaData);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedTablaData = getSortedTablaData();
  const totalPages = Math.ceil(sortedTablaData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedTablaData.slice(startIndex, endIndex);

  const handleSortTabla = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const clearSearch = () => { setSearchTerm(''); setCurrentPage(1); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedTablaData();
    const data = dataToExport.map(item => ({
      'Fecha': formatDate(item.FEC_OPE),
      'Grupo': item.GRUPO,
      'Ejecutivo': item.EJECUTIVO,
      'RUT Cliente': item.RUT_CLI,
      'Cliente': item.CLIENTE,
      'Monto Anticipado': Number(item.MON_ANT) || 0,
      'Tubo de Negocio': Number(item.MONTO_TUBO) || 0
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tubo de Negocio');
    
    XLSX.writeFile(wb, `tubo_negocio_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportTuboCincoDiasToExcel = () => {
    const data = tuboCincoDiasData.map(item => {
      const exportItem = {
        'Equipo': item.GRUPO,
        'Ejecutivo': item.EJECUTIVO
      };
      
      Object.keys(item).forEach(key => {
        if (key.startsWith('MON_ANT_')) {
          const fecha = key.replace('MON_ANT_', '');
          const val = item[key];
          exportItem[`Volumen ${fecha}`] = typeof val === 'number' ? val : (Number(val) || 0);
        } else if (key.startsWith('MONTO_TUBO_')) {
          const fecha = key.replace('MONTO_TUBO_', '');
          const val = item[key];
          exportItem[`Tubo ${fecha}`] = typeof val === 'number' ? val : (Number(val) || 0);
        }
      });
      
      return exportItem;
    });
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tubo Cinco Días');
    
    XLSX.writeFile(wb, `tubo_cinco_dias_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const transformTuboCincoDiasData = (rawData) => {
    if (!Array.isArray(rawData)) return [];
    
    return rawData.map(item => {
      const transformed = {
        GRUPO: item.GRUPO,
        EJECUTIVO: item.EJECUTIVO
      };

      for (let i = 1; i <= 5; i++) {
        const fecha = item[`FECHA_${i}`];
        const monAnt = item[`MON_ANT_${i}`];
        const montoTubo = item[`MONTO_TUBO_${i}`];

        if (fecha) {
          if (monAnt !== undefined) {
            transformed[`MON_ANT_${fecha}`] = monAnt;
          }
          if (montoTubo !== undefined) {
            transformed[`MONTO_TUBO_${fecha}`] = montoTubo;
          }
        }
      }

      return transformed;
    });
  };

  const getTuboCincoDiasColumns = () => {
    if (tuboCincoDiasData.length === 0) return [];
    return Object.keys(tuboCincoDiasData[0]);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const totalPagesDetalle = Math.ceil(tuboDetalleData.length / itemsPerPageDetalle);
  const startIndexDetalle = (currentPageDetalle - 1) * itemsPerPageDetalle;
  const endIndexDetalle = startIndexDetalle + itemsPerPageDetalle;
  const currentDataDetalle = tuboDetalleData.slice(startIndexDetalle, endIndexDetalle);

  const goToPageDetalle = (page) => {
    setCurrentPageDetalle(page);
  };

  const getPageNumbers = () => {
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const getPageNumbersDetalle = () => {
    const maxButtons = 7;
    if (totalPagesDetalle <= maxButtons) {
      return Array.from({ length: totalPagesDetalle }, (_, i) => i + 1);
    }
    
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPageDetalle - half);
    let end = Math.min(totalPagesDetalle, start + maxButtons - 1);
    
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // Cargar clientes
        const response = await getTuboListadoClientes(user);
        let clientesData = response;
        
        if (response.body && typeof response.body === 'string') {
          clientesData = JSON.parse(response.body);
        }
        
        setClientes(clientesData || []);
        
        // Cargar datos del gráfico
        const graficoResponse = await getTuboGrafico(user);
        let graficoData = graficoResponse;
        
        if (graficoResponse.body && typeof graficoResponse.body === 'string') {
          graficoData = JSON.parse(graficoResponse.body);
        }
        
        setChartData(graficoData || []);
        setChartLoading(false);
        
        // Cargar datos de la tabla
        const tablaResponse = await getTuboTabla(user);
        let tablaDataParsed = tablaResponse;
        
        if (tablaResponse.body && typeof tablaResponse.body === 'string') {
          tablaDataParsed = JSON.parse(tablaResponse.body);
        }
        
        setTablaData(tablaDataParsed || []);
        setTablaLoading(false);
        
        // Cargar datos tubo cinco días
        const tuboCincoDiasResponse = await getTuboCincoDias(user);
        let tuboCincoDiasParsed = tuboCincoDiasResponse;
        
        if (tuboCincoDiasResponse.body && typeof tuboCincoDiasResponse.body === 'string') {
          tuboCincoDiasParsed = JSON.parse(tuboCincoDiasResponse.body);
        }
        
        const transformedTuboCincoDias = transformTuboCincoDiasData(tuboCincoDiasParsed || []);
        setTuboCincoDiasData(transformedTuboCincoDias);
        setTuboCincoDiasLoading(false);
        
        // Cargar datos tabla detalle
        const tuboDetalleResponse = await getTuboTablaDetalle(user);
        let tuboDetalleParsed = tuboDetalleResponse;
        
        if (tuboDetalleResponse.body && typeof tuboDetalleResponse.body === 'string') {
          tuboDetalleParsed = JSON.parse(tuboDetalleResponse.body);
        }
        
        setTuboDetalleData(tuboDetalleParsed || []);
        setTuboDetalleLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setChartLoading(false);
        setTablaLoading(false);
        setTuboCincoDiasLoading(false);
        setTuboDetalleLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSave = async () => {
    if (!selectedCliente || !selectedDate || !monto.trim()) {
      alert('Por favor complete todos los campos');
      return;
    }
    const montoNum = parseMontoValue(monto);
    if (isNaN(montoNum)) {
      alert('Ingrese un monto válido');
      return;
    }

    const cliente = clientes.find(c => c.RUT_CLI === selectedCliente);
    if (!cliente) return;

    // Parse date components directly to avoid timezone issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    const anio = year;
    const mes = month;
    const dia = day;

    setSaving(true);
    try {
      await updateTuboNegocio(
        cliente.RUT_CLI,
        cliente.CLIENTE,
        user.ejecutivo_sistema_factoring,
        anio,
        mes,
        dia,
        montoNum
      );
      
      alert('Tubo de negocio guardado exitosamente');
      setSelectedCliente('');
      setSelectedDate('');
      setMonto('');
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('Error al guardar el tubo de negocio');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarRegistro = async (id) => {
    if (!confirm('¿Está seguro de que desea eliminar este registro?')) {
      return;
    }
    
    try {
      const response = await eliminarRegistroTubo(id);
      console.log('Respuesta eliminación:', response);
      
      // Verificar si la respuesta es exitosa
      let mensaje = null;
      if (response && response.body && typeof response.body === 'string') {
        try {
          const parsedBody = JSON.parse(response.body);
          mensaje = parsedBody.mensaje;
        } catch (e) {
          console.error('Error parseando body:', e);
        }
      }
      
      if (response && (response.statusCode === 200 || mensaje)) {
        // Actualizar tabla removiendo el registro eliminado
        setTuboDetalleData(prevData => prevData.filter(item => item.ID !== id));
        
        // Ajustar página si es necesario
        const newDataLength = tuboDetalleData.length - 1;
        const newTotalPages = Math.ceil(newDataLength / itemsPerPageDetalle);
        if (currentPageDetalle > newTotalPages && newTotalPages > 0) {
          setCurrentPageDetalle(newTotalPages);
        }
        
        // Recargar otros componentes
        const [graficoResponse, tablaResponse, tuboCincoDiasResponse] = await Promise.all([
          getTuboGrafico(user),
          getTuboTabla(user),
          getTuboCincoDias(user)
        ]);
        
        // Actualizar gráfico
        let graficoData = graficoResponse;
        if (graficoResponse.body && typeof graficoResponse.body === 'string') {
          graficoData = JSON.parse(graficoResponse.body);
        }
        setChartData(graficoData || []);
        
        // Actualizar tabla resumen
        let tablaDataParsed = tablaResponse;
        if (tablaResponse.body && typeof tablaResponse.body === 'string') {
          tablaDataParsed = JSON.parse(tablaResponse.body);
        }
        setTablaData(tablaDataParsed || []);
        
        // Actualizar tubo cinco días
        let tuboCincoDiasParsed = tuboCincoDiasResponse;
        if (tuboCincoDiasResponse.body && typeof tuboCincoDiasResponse.body === 'string') {
          tuboCincoDiasParsed = JSON.parse(tuboCincoDiasResponse.body);
        }
        const transformedTuboCincoDias = transformTuboCincoDiasData(tuboCincoDiasParsed || []);
        setTuboCincoDiasData(transformedTuboCincoDias);
        
        alert('Registro eliminado exitosamente');
      } else {
        console.log('Respuesta completa:', JSON.stringify(response, null, 2));
        throw new Error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error al eliminar registro:', error);
      alert('Error al eliminar el registro');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tubo de negocio</h1>
      </div>    
      {user?.tipo_usuario === 'Ejecutivo' && (
        <div className="seccion-datos mb-6">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
                Ingresar de Tubo de Negocios
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Calendario */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Listado de Clientes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cliente
              </label>
              {loading ? (
                <div className="text-gray-500">Cargando clientes...</div>
              ) : (
                <select
                  value={selectedCliente}
                  onChange={(e) => setSelectedCliente(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccione un cliente</option>
                  {clientes.map((cliente) => (
                    <option key={cliente.RUT_CLI} value={cliente.RUT_CLI}>
                      {cliente.CLIENTE}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Input Numérico */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monto
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={(e) => setMonto(formatMontoDisplay(e.target.value))}
                placeholder="Ingrese el monto"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Botón Guardar */}
          <div className="mt-6">
            <button
              onClick={handleSave}
              disabled={saving || !selectedCliente || !selectedDate || !monto.trim()}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {/* Gráfico de Área */}
      <div className="seccion-datos">
        <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
              Monto Anticipado (Sistema Factoring) VS Tubo de Negocio 
        </h3>
        {chartLoading ? (
          <div className="flex justify-center items-center h-64">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--if-gray-300)" />
              <XAxis 
                dataKey="FECHA" 
                tick={{ fontSize: 10, fill: 'var(--if-text-secondary)', angle: -45, textAnchor: 'end' }}
                axisLine={{ stroke: 'var(--if-gray-400)' }}
                tickLine={{ stroke: 'var(--if-gray-400)' }}
                height={60}
                interval={0}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: 'var(--if-text-secondary)' }}
                axisLine={{ stroke: 'var(--if-gray-400)' }}
                tickLine={{ stroke: 'var(--if-gray-400)' }}
                tickFormatter={(value) => value.toLocaleString('es-CL')}
              />
              <Tooltip 
                formatter={(value, name) => {
                  const formattedValue = typeof value === 'string' ? 
                    parseFloat(value).toLocaleString('es-CL') : 
                    value.toLocaleString('es-CL');
                  return [`$${formattedValue}`, name];
                }}
                labelFormatter={(label) => `Fecha: ${label}`}
                contentStyle={{ backgroundColor: 'white', border: '1px solid var(--if-gray-300)', color: 'var(--if-text-primary)' }}
              />
              <Legend 
                wrapperStyle={{ color: 'var(--if-text-primary)' }}
                align="right"
                verticalAlign="top"
                iconType="rect"
              />
              <Area 
                type="linear" 
                dataKey="MON_ANT" 
                stroke="var(--if-accent)" 
                fill="var(--if-accent)" 
                fillOpacity={0.4}
                name="Monto Anticipado"
              />
              <Area 
                type="linear" 
                dataKey="MONTO_TUBO" 
                stroke="var(--if-primary)" 
                fill="var(--if-primary)" 
                fillOpacity={0.4}
                name="Tubo de Negocio"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabla de Datos */}
      <div className="seccion-datos mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
              Resumen Monto Anticipado (Sistema Factoring) VS Tubo de Negocio
          </h3>
          {tablaData.length > 0 && (
            <button
              onClick={exportToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        {tablaData.length > 0 && (
          <div className="mb-4">
            <div className="relative" style={{ maxWidth: '500px' }}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
              </div>
              <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {searchTerm && <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
            </div>
            {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{sortedTablaData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedTablaData.length} resultado{sortedTablaData.length !== 1 ? 's' : ''} encontrado{sortedTablaData.length !== tablaData.length ? ` de ${tablaData.length} registros` : ''}</span>}</div>}
          </div>
        )}
        {tablaLoading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : tablaData.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : sortedTablaData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
          <div className="tabla-container">
            <table style={{fontSize: '0.875rem'}}>
              <thead>
                <tr>
                  <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('FEC_OPE')}>Fecha {getSortIcon('FEC_OPE')}</th>
                  <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('GRUPO')}>Equipo {getSortIcon('GRUPO')}</th>
                  <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('EJECUTIVO')}>Ejecutivo {getSortIcon('EJECUTIVO')}</th>
                  <th style={{width: '90px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('RUT_CLI')}>RUT Cliente {getSortIcon('RUT_CLI')}</th>
                  <th style={{width: '300px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('CLIENTE')}>Cliente {getSortIcon('CLIENTE')}</th>
                  <th style={{textAlign: 'right', width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('MON_ANT')}>Monto Anticipado {getSortIcon('MON_ANT')}</th>
                  <th style={{textAlign: 'right', width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSortTabla('MONTO_TUBO')}>Tubo de Negocio {getSortIcon('MONTO_TUBO')}</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.FEC_OPE)}</td>
                    <td>{item.GRUPO}</td>
                    <td>{item.EJECUTIVO}</td>
                    <td>{item.RUT_CLI}</td>
                    <td>{item.CLIENTE}</td>
                    <td style={{textAlign: 'right'}}>
                      ${item.MON_ANT?.toLocaleString('es-CL') || '0'}
                    </td>
                    <td style={{textAlign: 'right'}}>
                      ${typeof item.MONTO_TUBO === 'string' ? parseFloat(item.MONTO_TUBO).toLocaleString('es-CL') : (item.MONTO_TUBO || 0).toLocaleString('es-CL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginación */}
        {sortedTablaData.length > itemsPerPage && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndex + 1} a {Math.min(endIndex, sortedTablaData.length)} de {sortedTablaData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabla Tubo Cinco Días */}
      <div className="seccion-datos mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
            Tubo Cinco días
          </h3>
          {tuboCincoDiasData.length > 0 && (
            <button
              onClick={exportTuboCincoDiasToExcel}
              className="btn-primary"
              style={{
                background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                borderLeft: '3px solid var(--if-primary)'
              }}
            >
              <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
              Exportar Excel
            </button>
          )}
        </div>
        
        {tuboCincoDiasLoading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : tuboCincoDiasData.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : (
          <div className="tabla-container" style={{overflowX: 'auto'}}>
            <table style={{fontSize: '0.875rem'}}>
              <thead>
                <tr>
                  <th style={{position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10, minWidth: '100px'}}>Equipo</th>
                  <th style={{position: 'sticky', left: '100px', backgroundColor: 'white', zIndex: 10, minWidth: '250px'}}>Ejecutivo</th>
                  {getTuboCincoDiasColumns().filter(col => col !== 'GRUPO' && col !== 'EJECUTIVO').map((column) => {
                    let displayName = column;
                    if (column.startsWith('MON_ANT_')) {
                      displayName = `Volumen ${column.replace('MON_ANT_', '')}`;
                    } else if (column.startsWith('MONTO_TUBO_')) {
                      displayName = `Tubo ${column.replace('MONTO_TUBO_', '')}`;
                    }
                    return (
                      <th key={column} style={{minWidth: '120px', textAlign: 'right'}}>
                        {displayName}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {tuboCincoDiasData.map((row, index) => (
                  <tr key={index}>
                    <td style={{position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 5}}>{row.GRUPO || '-'}</td>
                    <td style={{position: 'sticky', left: '100px', backgroundColor: 'white', zIndex: 5, width: '250px'}}>{row.EJECUTIVO || '-'}</td>
                    {getTuboCincoDiasColumns().filter(col => col !== 'GRUPO' && col !== 'EJECUTIVO').map((column) => (
                      <td key={column} style={{textAlign: 'right'}}>
                        {row[column] && !isNaN(row[column]) 
                          ? `$${Number(row[column]).toLocaleString('de-DE')}`
                          : row[column] || '-'
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tabla Detalle Tubo */}
      <div className="seccion-datos mt-6">
        <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600', borderLeft: '4px solid var(--if-accent)', paddingLeft: '1rem'}}>
          Detalle Tubo de Negocio
        </h3>
        
        {tuboDetalleLoading ? (
          <div className="flex justify-center items-center h-32">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
          </div>
        ) : tuboDetalleData.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : (
          <div className="tabla-container">
            <table style={{fontSize: '0.875rem'}}>
              <thead>
                <tr>
                  <th style={{width: '120px'}}>Ejecutivo</th>
                  <th style={{width: '100px'}}>Equipo</th>
                  <th style={{width: '90px'}}>RUT Cliente</th>
                  <th style={{width: '300px'}}>Cliente</th>
                  <th style={{width: '80px'}}>Fecha</th>
                  <th style={{textAlign: 'right', width: '120px'}}>Monto Tubo</th>
                  <th style={{width: '80px', textAlign: 'center'}}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {currentDataDetalle.map((item, index) => (
                  <tr key={index}>
                    <td>{item.EJECUTIVO}</td>
                    <td>{item.GRUPO}</td>
                    <td>{item.RUT_CLIENTE}</td>
                    <td>{item.CLIENTES}</td>
                    <td>{formatDate(item.FECHA)}</td>
                    <td style={{textAlign: 'right'}}>
                      ${item.MONTO_TUBO?.toLocaleString('de-DE') || '0'}
                    </td>
                    <td style={{textAlign: 'center'}}>
                      <button
                        onClick={() => handleEliminarRegistro(item.ID)}
                        className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                        title="Eliminar registro"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginación Detalle */}
        {tuboDetalleData.length > itemsPerPageDetalle && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndexDetalle + 1} a {Math.min(endIndexDetalle, tuboDetalleData.length)} de {tuboDetalleData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPageDetalle(currentPageDetalle - 1)}
                disabled={currentPageDetalle === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              {getPageNumbersDetalle().map(page => (
                <button
                  key={page}
                  onClick={() => goToPageDetalle(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPageDetalle === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => goToPageDetalle(currentPageDetalle + 1)}
                disabled={currentPageDetalle === totalPagesDetalle}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IngresarTuboNegocio;